import { useEffect } from 'react';
import { useRouter } from '@/domains/shared/router/Router';
import { useDiagnostic } from '@/contexts/DiagnosticContext';
import { Progress } from '@/domains/shared/components/Progress';
import { RadioScale } from '@/domains/shared/components/RadioScale';
import { Button } from '@/domains/shared/components/Button';
import { TOTAL_QUESTIONS_COUNT } from '@/domains/assessment/constants';
import type { DiagnosticScaleValue } from '@/domains/assessment/types';

export function QuestionStepperPage() {
  const { navigate } = useRouter();
  const {
    currentQuestionIndex,
    flatQuestions,
    getAnswerForCurrentQuestion,
    setAnswer,
    nextQuestion,
    prevQuestion,
  } = useDiagnostic();

  const currentQ = flatQuestions[currentQuestionIndex];
  const currentValue = getAnswerForCurrentQuestion();
  const currentStepNumber = currentQuestionIndex + 1;
  const progressPercent = (currentStepNumber / TOTAL_QUESTIONS_COUNT) * 100;

  // Atalho de teclado: números 0, 1, 2, 3 selecionam a opção diretamente
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (['0', '1', '2', '3'].includes(e.key) && currentQ) {
        setAnswer(currentQ.categoryKey, currentQ.questionIndex, Number(e.key) as DiagnosticScaleValue);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [currentQ, setAnswer]);

  if (!currentQ) {
    return null;
  }

  const handleNext = () => {
    if (currentValue === null) return;

    const advanced = nextQuestion();
    if (!advanced) {
      // Era a última pergunta (pergunta 18), segue para identificação
      navigate('/identificacao');
    }
  };

  const handleBack = () => {
    const receded = prevQuestion();
    if (!receded) {
      // Pergunta 1: volta para as instruções
      navigate('/instrucoes');
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-navy text-brand-offWhite">
      {/* Header com indicador de etapa e botão de retorno */}
      <header className="border-b border-white/10 bg-navy-dark/90 px-4 py-3.5 sticky top-0 z-40 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between gap-4">
          <button
            type="button"
            onClick={handleBack}
            className="inline-flex min-h-[44px] items-center gap-1.5 text-xs font-semibold text-brand-offWhite/80 hover:text-white transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-orange rounded-lg px-2"
            aria-label="Voltar para a pergunta anterior ou orientações"
          >
            <span>←</span> Voltar
          </button>

          <span className="text-xs font-bold text-brand-orange uppercase tracking-wider">
            {currentQ.categoryName}
          </span>
        </div>
      </header>

      {/* Barra de progresso visível no topo */}
      <div className="border-b border-white/5 bg-navy-dark px-4 py-3">
        <div className="mx-auto max-w-2xl">
          <Progress
            value={progressPercent}
            currentStep={currentStepNumber}
            totalSteps={TOTAL_QUESTIONS_COUNT}
            showPercentage
          />
        </div>
      </div>

      {/* Conteúdo Principal: Uma pergunta por tela */}
      <main className="mx-auto flex w-full max-w-2xl flex-1 flex-col justify-between px-4 py-8 sm:py-12">
        <div className="text-left">
          {/* Badge da Categoria */}
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg bg-white/5 border border-white/10 px-3 py-1 text-xs font-semibold text-brand-orange">
            <span>Dimensão {currentQ.categoryName}</span>
            <span className="text-white/40">•</span>
            <span className="text-brand-offWhite/70">Pergunta {currentQ.questionIndex + 1} de 3</span>
          </div>

          {/* Texto da Pergunta em destaque */}
          <h1 className="text-xl font-bold tracking-tight text-white sm:text-2xl sm:leading-snug">
            {currentQ.text}
          </h1>

          <p className="mt-2 text-xs text-brand-offWhite/60 sm:text-sm">
            Selecione a opção que melhor retrata o momento atual do seu escritório:
          </p>

          {/* Escala de Opções (0 a 3) com alvo mínimo de 56px */}
          <div className="mt-8">
            <RadioScale
              name={`question-${currentQ.globalIndex}`}
              value={currentValue}
              options={currentQ.options}
              onChange={(val) => setAnswer(currentQ.categoryKey, currentQ.questionIndex, val)}
            />
          </div>
        </div>

        {/* Rodapé com Ação de Avanço */}
        <div className="mt-10 flex flex-col gap-3 border-t border-white/10 pt-6">
          <Button
            variant="primary"
            size="lg"
            disabled={currentValue === null}
            onClick={handleNext}
            className="w-full text-base font-bold shadow-lg"
          >
            {currentStepNumber === TOTAL_QUESTIONS_COUNT ? (
              'Finalizar e Ver Meu Diagnóstico →'
            ) : (
              'Próxima Pergunta →'
            )}
          </Button>

          <p className="text-center text-[11px] text-brand-offWhite/50">
            Dica: você também pode pressionar as teclas <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono">0</kbd>, <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono">1</kbd>, <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono">2</kbd> ou <kbd className="px-1.5 py-0.5 rounded bg-white/10 text-white font-mono">3</kbd> no teclado.
          </p>
        </div>
      </main>
    </div>
  );
}
